import { NUCLEO_ICON_NAMES } from "./nucleo-icon-names"

export const DEFAULT_ARTICLE_ICON = "Page2"

// Word-level synonyms expand each label word so common search terms surface
// icons that don't literally include the word ("delete" finds `Trash`).
export const NUCLEO_ICON_WORD_SYNONYMS: Record<string, string> = {
	trash: "delete remove bin discard",
	delete: "trash remove bin",
	remove: "trash delete",
	bin: "trash delete",
	user: "person account profile member",
	person: "user account profile",
	profile: "user person account",
	account: "user person profile",
	users: "people members team",
	people: "users team members",
	team: "users people members",
	gear: "settings cog preferences config",
	settings: "gear cog preferences config options",
	cog: "gear settings",
	sliders: "settings filters adjust",
	envelope: "email mail message inbox",
	email: "envelope mail message inbox",
	mail: "envelope email message",
	bell: "notification notifications alert",
	notification: "bell alert",
	alert: "notification bell warning",
	warning: "alert caution exclamation",
	book: "docs documentation read article",
	docs: "book documentation article",
	bolt: "lightning power energy zap fast",
	zap: "bolt lightning fast",
	credit: "billing payment card",
	card: "credit billing payment",
	cube: "box package 3d",
	box: "cube package",
	code: "developer programming script bracket",
	globe: "world internet web earth language",
	language: "globe translate locale",
	clock: "time hour schedule recent",
	calendar: "schedule date day",
	tag: "label",
	label: "tag",
	repeat: "refresh cycle loop sync",
	refresh: "repeat reload",
	chart: "graph analytics report",
	graph: "chart analytics",
	star: "favorite",
	heart: "favorite love like",
	home: "house",
	magnifier: "search find",
	search: "magnifier find",
	shield: "security protect lock",
	lock: "shield security private",
	key: "password lock",
	chat: "message comment talk speech",
	message: "chat comment speech",
	atom: "ai bot molecule",
	file: "doc document",
	doc: "file document",
	image: "photo picture",
	check: "tick done complete success",
	cross: "close cancel error fail",
	close: "cross cancel",
	plus: "add new",
	add: "plus new",
	minus: "subtract remove",
	info: "information help",
	question: "help info",
	help: "question info support",
	copy: "duplicate clone",
	clipboard: "paste copy",
	bookmark: "save favorite",
	cloud: "storage server",
	database: "storage server",
	bug: "issue insect ticket",
	office: "building work company",
	building: "office company",
	import: "upload arrow",
	export: "download arrow",
	signature: "sign autograph pen",
	quote: "quotation citation speech",
	msgs: "messages chat comments",
	pencil: "edit write",
	edit: "pencil write",
}

export const splitPascalCase = (name: string): string =>
	name.replace(/(?<!^)(?=[A-Z])/g, " ")

export function expandIconSynonyms(label: string): string {
	const words = label.toLowerCase().split(/\s+/).filter(Boolean)
	const extras = new Set<string>()
	for (const word of words) {
		const syn = NUCLEO_ICON_WORD_SYNONYMS[word]
		if (syn) {
			for (const term of syn.split(/\s+/)) extras.add(term)
		}
	}
	if (extras.size === 0) return label.toLowerCase()
	return `${label.toLowerCase()} ${[...extras].join(" ")}`
}

export function iconSearchText(root: string): string {
	return `${expandIconSynonyms(splitPascalCase(root))} ${root.toLowerCase()}`
}

const NAME_SET: ReadonlySet<string> = new Set(NUCLEO_ICON_NAMES)

export function isNucleoIconName(
	name: string | null | undefined,
): name is string {
	return typeof name === "string" && NAME_SET.has(name)
}

export function articleIconOrDefault(name: string | null | undefined): string {
	return isNucleoIconName(name) ? name : DEFAULT_ARTICLE_ICON
}

interface IndexedIcon {
	name: string
	labelWords: string[]
	synonymWords: Set<string>
}

let index: IndexedIcon[] | null = null

function buildIndex(): IndexedIcon[] {
	return NUCLEO_ICON_NAMES.map((name) => {
		const label = splitPascalCase(name).toLowerCase()
		const labelWords = label.split(/\s+/).filter(Boolean)
		const synonymWords = new Set(
			expandIconSynonyms(label)
				.split(/\s+/)
				.filter((w) => w && !labelWords.includes(w)),
		)
		return { name, labelWords, synonymWords }
	})
}

function scoreIcon(icon: IndexedIcon, tokens: string[]): number {
	let score = 0
	for (const token of tokens) {
		if (icon.labelWords.includes(token)) score += 4
		else if (icon.labelWords.some((w) => w.startsWith(token))) score += 2
		else if (icon.synonymWords.has(token)) score += 2
		else if (icon.name.toLowerCase().includes(token)) score += 1
	}
	return score
}

// Ranks the catalogue for a free-text query. Ties go to the shorter name so the
// plain object ("Book") outranks its variants ("BookOpen4").
export function searchNucleoIcons(query: string, limit = 20): string[] {
	const tokens = [
		...new Set(
			query
				.toLowerCase()
				.split(/[^a-z0-9]+/)
				.filter((t) => t.length > 1),
		),
	]
	if (tokens.length === 0) return []
	index ??= buildIndex()
	return index
		.map((icon) => ({ name: icon.name, score: scoreIcon(icon, tokens) }))
		.filter((r) => r.score > 0)
		.sort(
			(a, b) =>
				b.score - a.score ||
				a.name.length - b.name.length ||
				a.name.localeCompare(b.name),
		)
		.slice(0, limit)
		.map((r) => r.name)
}
